import React, { useState, useEffect, useRef } from "react";
import Layout from "../components/Layout";
import api from "../api";
import * as XLSX from "xlsx";
import { OfflineViewingCachedDataBanner } from "../useIsOnline";

const HCP_REVIEW_FORMATS = ['Singles Stableford', 'Singles Medal', 'Singles Stroke Play'];
const TEE_COLORS = ['White', 'Yellow (Men)', 'Yellow (Ladies)', 'Red (Men)', 'Red (Ladies)'];

export default function Players() {
  const [players, setPlayers] = useState([]);
  const [event, setEvent] = useState(null);
  const [eventPlayers, setEventPlayers] = useState({});
  const [teams, setTeams] = useState([]);
  const [teamAName, setTeamAName] = useState("");
  const [teamBName, setTeamBName] = useState("");
  const [newName, setNewName] = useState("");
  const [newHcp, setNewHcp] = useState("");
  const [newTeeColor, setNewTeeColor] = useState("");
  const [courses, setCourses] = useState([]);
  const [editHcp, setEditHcp] = useState({});
  const [editHcpAdj, setEditHcpAdj] = useState({});
  const [loading, setLoading] = useState(true);
  const [saved, setSaved] = useState("");
  const [error, setError] = useState("");
  const [selectedId, setSelectedId] = useState(null);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [saveGameName, setSaveGameName] = useState("");
  const [saveBusy, setSaveBusy] = useState(false);
  const [showLoadModal, setShowLoadModal] = useState(false);
  const [savedGames, setSavedGames] = useState([]);
  // Real competitions (the events table) shown in the SAME modal per Mike's
  // 2026-09-14 request -- deliberately kept visually separate from Saved
  // Games below: those are reusable setup templates, these are actual dated
  // competitions players see and register for in the Terminal. Deleting one
  // here removes it from Terminal's list immediately (same events table,
  // no separate sync needed).
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const [loadBusy, setLoadBusy] = useState(false);
  const [voiceListening, setVoiceListening] = useState(false);
  const [voiceTranscript, setVoiceTranscript] = useState("");
  const [voiceBusy, setVoiceBusy] = useState(false);
  const [voiceError, setVoiceError] = useState("");
  const [voiceReview, setVoiceReview] = useState(null); // array of {name, handicap, include} once parsed
  const [excelBusy, setExcelBusy] = useState(false);
  const [excelError, setExcelError] = useState("");
  const [excelReview, setExcelReview] = useState(null); // array of {name, handicap, include} once parsed
  const excelFileInputRef = useRef(null);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  // Electron's bundled Chromium lacks the Google API key that real Chrome uses
  // to authorize speech-recognition requests, so voice input always fails
  // with a "network" error there regardless of connectivity — window.electronAPI
  // (exposed only by preload.js inside the desktop app) is how we tell the two
  // shells apart and hide the feature rather than offer something that can't work.
  const isElectron = typeof window !== "undefined" && !!window.electronAPI;
  const voiceRecognitionRef = useRef(null);
  const eventRef = useRef(null);
  const saveGameInputRef = useRef(null);

  // Electron's window-level focus handling can race against a bare
  // autoFocus attribute — the input can end up LOOKING focused (blinking
  // cursor) without actually holding real keyboard focus, so keystrokes
  // never land. Never observed on the web app (no window-level focus layer
  // there), only in the offline desktop app. Forcing focus explicitly,
  // slightly after the modal's own mount/paint, sidesteps the race.
  useEffect(() => {
    if (showSaveModal) {
      const t = setTimeout(() => saveGameInputRef.current?.focus(), 50);
      return () => clearTimeout(t);
    }
  }, [showSaveModal]);
  const today = new Date().toISOString().split("T")[0];
  const gameTypes = ['Singles Stableford','Singles Stableford (Full Handicap)','Singles Medal','Singles Stroke Play','Singles Match Play','Skins','Better Ball Stableford','Better Ball Medal','Better Ball Stroke Play','Better Ball Match Play','Fourball Better Ball Stableford','Team Stableford (Best 2 of 4)','Fourball Match Play','Fourball Stroke Play','Foursomes Stableford','Foursomes Medal','Foursomes Match Play','Greensomes Stableford','Greensomes Medal','Greensomes Match Play','Team Stableford','Team Medal','Team Aggregate Stableford','Team Aggregate Stroke Play','Team Match Play','Texas Scramble (4 players)','Texas Scramble (3 players)','Texas Scramble Medal (4 players)','Texas Scramble Medal (3 players)'];

  useEffect(() => { loadPlayers(); loadOrCreateEvent(); loadTeams(); loadCourses(); }, []);

  const loadCourses = async () => {
    try {
      const res = await api.get("/courses");
      setCourses(res.data || []);
    } catch(e) { console.error("Load courses:", e.message); }
  };

  // Voice input needs a live connection to the browser's speech-recognition
  // backend (it's cloud-based, not on-device) — gate the button on real
  // connectivity so the offline desktop app doesn't offer something that
  // can only ever fail with no signal.
  useEffect(() => {
    const goOnline = () => setIsOnline(true);
    const goOffline = () => setIsOnline(false);
    window.addEventListener("online", goOnline);
    window.addEventListener("offline", goOffline);
    return () => { window.removeEventListener("online", goOnline); window.removeEventListener("offline", goOffline); };
  }, []);

  // Keep the ref in sync with every render, not just after a successful save —
  // otherwise saving one field (e.g. Format, changed via onChange) reads a
  // stale eventRef still missing an in-progress edit in another field (e.g.
  // Venue, only saved on blur), and silently overwrites it back to empty.
  useEffect(() => { eventRef.current = event; }, [event]);

  const loadTeams = async () => {
    try {
      const res = await api.get("/teams");
      if (res.data.teams && res.data.teams.length >= 2) {
        setTeams(res.data.teams);
        setTeamAName(res.data.teams[0].team_name);
        setTeamBName(res.data.teams[1].team_name);
      }
    } catch(e) { console.error("Load teams:", e.message); }
  };

  const saveTeamNames = async () => {
    try {
      const res = await api.post("/teams/setup", { teamAName, teamBName });
      setTeams([res.data.teamA, res.data.teamB]);
    } catch(e) { console.error("Save teams:", e.message); }
  };

  const loadPlayers = async () => {
    try {
      const res = await api.get("/players");
      setPlayers(res.data);
      setError("");
    } catch(e) {
      setError("Failed to load players: " + (e.response?.data?.error || e.message));
    } finally { setLoading(false); }
  };

  const loadOrCreateEvent = async () => {
    try {
      const res = await api.get("/events/current");
      if (res.data) {
        eventRef.current = res.data;
        setEvent(res.data);
        localStorage.setItem('gameType', res.data.game_type || '');
        const epRes = await api.get("/events/" + res.data.id + "/players");
        const epMap = {};
        epRes.data.forEach(ep => { epMap[ep.player_id] = ep; });
        setEventPlayers(epMap);
      } else {
        const created = await api.post("/events", { date: today, game_type: "Singles Stableford", scoring_method: "Stableford", handicap_cap: null, venue: "" });
        eventRef.current = created.data;
        setEvent(created.data);
        localStorage.setItem('gameType', created.data.game_type || '');
      }
    } catch(e) { console.log("Event error:", e.message); }
  };

  const saveEvent = async (field, value) => {
    const current = eventRef.current;
    try {
      if (!current) {
        const res = await api.post("/events", { date: today, [field]: value, game_type: "Singles Stableford", scoring_method: "Stableford", handicap_cap: null, venue: "" });
        eventRef.current = res.data;
        setEvent(res.data);
      } else {
        const updated = { ...current, [field]: value };
        const res = await api.put("/events/" + current.id, updated);
        eventRef.current = res.data;
        setEvent(res.data);
      }
      // course_id and handicap_cap both feed hcp_adj server-side -- without
      // re-fetching, the roster keeps showing whatever Course HCP figures were
      // computed at page-load time, silently stale until a manual reload.
      // Found live 2026-09-08: linking a course correctly updated the event
      // but every player's Course HCP on screen stayed unchanged until the page
      // was refreshed by hand.
      if (field === "course_id" || field === "handicap_cap") await loadPlayers();
      // Layout.jsx (the sidebar) only checks the event's format on page
      // navigation -- changing Format here without leaving the page never
      // told it, so Teams/Pairs stayed greyed out for whatever format was
      // active at page load. Found live 2026-09-09 (switched to Team
      // Stableford, Teams nav stayed disabled). See Layout.jsx's listener.
      if (field === "game_type") window.dispatchEvent(new Event("gsm-event-format-changed"));
    } catch(e) { console.error("Save event:", e.message); }
  };

  const showSaved = (msg) => { setSaved(msg); setTimeout(() => setSaved(""), 2500); };

  const updatePlayers = async () => {
    try {
      await loadPlayers();
      showSaved("Players updated!");
      await loadOrCreateEvent();
    } catch(e) { alert("Failed to update players"); }
  };

  const resetGame = async () => {
    if (!window.confirm("Reset game? This will clear all groups, scores and event data.")) return;
    try {
      await api.post("/events/reset");
      eventRef.current = null;
      setEvent(null);
      setEventPlayers({});
      setTeams([]);
      setTeamAName("");
      setTeamBName("");
      await loadOrCreateEvent();
    } catch(e) { alert("Failed to reset game: " + e.message); }
  };

  const openSaveModal = async () => {
    setSaveGameName("");
    setSavedGames([]);
    setShowSaveModal(true);
    try {
      const res = await api.get("/savedgames");
      setSavedGames(res.data);
    } catch(e) { /* the "save as new" flow below still works even if this list fails to load */ }
    finally {
      // The saved-games list above the input can take a moment to load
      // (especially offline) and, once it arrives, inserts a whole new
      // section above the input — in the Electron desktop app specifically,
      // that later DOM change can silently steal keyboard focus back even
      // though the input itself never unmounts. Re-focus explicitly after
      // this settles, not just once when the modal first opens.
      setTimeout(() => saveGameInputRef.current?.focus(), 50);
    }
  };

  const doSaveGame = async () => {
    if (!saveGameName.trim()) { showSaved("Please enter a name"); return; }
    setSaveBusy(true);
    try {
      await api.post("/savedgames/save", { name: saveGameName.trim() });
      setShowSaveModal(false);
      showSaved(`Game saved as "${saveGameName.trim()}"`);
    } catch(e) { alert("Failed to save game: " + (e.response?.data?.error || e.message)); }
    finally { setSaveBusy(false); }
  };

  const doUpdateGame = async (game) => {
    if (!window.confirm(`Update "${game.name}" with the current roster, groups and settings? This overwrites what was previously saved under this name.`)) return;
    setSaveBusy(true);
    try {
      await api.post("/savedgames/" + game.id + "/update");
      setShowSaveModal(false);
      showSaved(`Updated "${game.name}"!`);
    } catch(e) { alert("Failed to update saved game: " + (e.response?.data?.error || e.message)); }
    finally { setSaveBusy(false); }
  };

  const openLoadModal = async () => {
    try {
      const res = await api.get("/savedgames");
      setSavedGames(res.data);
      const upcoming = await api.get("/events/upcoming");
      setUpcomingEvents(upcoming.data);
      setShowLoadModal(true);
    } catch(e) { alert("Failed to load saved games list"); }
  };

  const deleteCompetition = async (ev, e) => {
    e.stopPropagation();
    if (!window.confirm(`Delete the "${ev.venue}" competition on ${new Date(ev.date).toLocaleDateString("en-GB")}? This removes it everywhere, including the Terminal.`)) return;
    try {
      await api.delete("/events/" + ev.id);
      setUpcomingEvents(prev => prev.filter(x => x.id !== ev.id));
    } catch(err) {
      if (err.response?.status === 409 && err.response.data.isActive) {
        alert(err.response.data.error);
      } else if (err.response?.status === 409) {
        if (window.confirm(`${err.response.data.error} (${err.response.data.scoreCount} hole score(s)). Delete anyway? This cannot be undone.`)) {
          try {
            await api.delete("/events/" + ev.id + "?force=true");
            setUpcomingEvents(prev => prev.filter(x => x.id !== ev.id));
          } catch(e2) { alert("Failed to delete competition"); }
        }
      } else {
        alert("Failed to delete competition");
      }
    }
  };

  const doLoadGame = async (game) => {
    if (!window.confirm(`Load "${game.name}"? This replaces the current event and overwrites the handicap of each of its ${game.player_count} saved player(s) with the value saved at the time.`)) return;
    setLoadBusy(true);
    try {
      await api.post("/savedgames/" + game.id + "/load");
      setShowLoadModal(false);
      eventRef.current = null;
      setEvent(null);
      setEventPlayers({});
      setTeams([]);
      setTeamAName("");
      setTeamBName("");
      await loadPlayers();
      await loadOrCreateEvent();
      await loadTeams();
      showSaved(`Loaded "${game.name}"!`);
    } catch(e) { alert("Failed to load game: " + (e.response?.data?.error || e.message)); }
    finally { setLoadBusy(false); }
  };

  const deleteSavedGame = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm("Delete this saved game?")) return;
    try {
      await api.delete("/savedgames/" + id);
      setSavedGames(prev => prev.filter(g => g.id !== id));
    } catch(e) { alert("Failed to delete"); }
  };

  const addPlayer = async () => {
    if (!newName.trim()) return;
    try {
      const res = await api.post("/players", { name: newName.trim(), handicap: parseInt(newHcp) || 0, tee_color: newTeeColor });
      setPlayers(prev => [...prev, res.data].sort((a,b) => a.name.localeCompare(b.name)));
      setNewName(""); setNewHcp(""); setNewTeeColor("");
    } catch(e) { alert("Failed to add player: " + (e.response?.data?.error || e.message)); }
  };

  const startVoiceInput = () => {
    if (isElectron) {
      setVoiceError("Voice input isn't available in the desktop app — use the web version at golfsocietymanager.com instead.");
      return;
    }
    if (!isOnline) {
      setVoiceError("Voice input needs an internet connection — try again when you have signal.");
      return;
    }
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) {
      setVoiceError("Voice input needs Chrome or Edge — not supported in this browser.");
      return;
    }
    setVoiceError("");
    setVoiceTranscript("");
    const recognition = new SpeechRecognition();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = "en-GB";
    let finalTranscript = "";
    recognition.onresult = (e) => {
      let interim = "";
      for (let i = e.resultIndex; i < e.results.length; i++) {
        const chunk = e.results[i][0].transcript;
        if (e.results[i].isFinal) finalTranscript += chunk + " ";
        else interim += chunk;
      }
      setVoiceTranscript(finalTranscript + interim);
    };
    recognition.onerror = (e) => setVoiceError("Voice input error: " + e.error);
    recognition.onend = () => setVoiceListening(false);
    voiceRecognitionRef.current = recognition;
    recognition.start();
    setVoiceListening(true);
  };

  const stopVoiceInput = async () => {
    if (voiceRecognitionRef.current) voiceRecognitionRef.current.stop();
    setVoiceListening(false);
    if (!voiceTranscript.trim()) return;
    setVoiceBusy(true);
    setVoiceError("");
    try {
      const res = await api.post("/players/voice-parse", { transcript: voiceTranscript });
      const parsed = (res.data.players || []).map(p => ({ name: p.name || "", handicap: p.handicap || 0, include: true }));
      if (!parsed.length) setVoiceError("Couldn't make out any players from that — try again, speaking clearly.");
      setVoiceReview(parsed);
    } catch(e) {
      setVoiceError("Failed to process: " + (e.response?.data?.error || e.message));
    } finally { setVoiceBusy(false); }
  };

  const confirmVoicePlayers = async () => {
    const toAdd = (voiceReview || []).filter(p => p.include && p.name.trim());
    for (const p of toAdd) {
      try {
        const res = await api.post("/players", { name: p.name.trim(), handicap: parseInt(p.handicap) || 0 });
        setPlayers(prev => [...prev, res.data].sort((a,b) => a.name.localeCompare(b.name)));
      } catch(e) { console.error("Voice add player failed:", p.name, e.message); }
    }
    setVoiceReview(null);
    setVoiceTranscript("");
  };

  const pickExcelFile = () => excelFileInputRef.current?.click();

  const handleExcelFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow re-selecting the same file later
    if (!file) return;
    setExcelError("");
    setExcelBusy(true);
    try {
      const buf = await file.arrayBuffer();
      const wb = XLSX.read(buf, { type: "array" });
      const ws = wb.Sheets[wb.SheetNames[0]];
      // header:1 keeps the sheet's real layout (title rows, blank rows,
      // extra columns and all) instead of guessing a header row ourselves —
      // that guesswork is exactly what we're asking the AI to do instead,
      // since real club spreadsheets are rarely a clean name/handicap table.
      const rows = XLSX.utils.sheet_to_json(ws, { header: 1, raw: false, defval: "" });
      if (!rows.length) { setExcelError("That file has no rows to import."); return; }
      const res = await api.post("/players/excel-parse", { rows });
      const parsed = (res.data.players || []).map(p => ({ name: p.name || "", handicap: p.handicap || 0, include: true }));
      if (!parsed.length) setExcelError("Couldn't find any players in that spreadsheet — check it has a name column.");
      setExcelReview(parsed);
    } catch(e) {
      setExcelError("Failed to read file: " + (e.response?.data?.error || e.message));
    } finally { setExcelBusy(false); }
  };

  const confirmExcelPlayers = async () => {
    const toAdd = (excelReview || []).filter(p => p.include && p.name.trim());
    for (const p of toAdd) {
      try {
        const res = await api.post("/players", { name: p.name.trim(), handicap: parseInt(p.handicap) || 0 });
        setPlayers(prev => [...prev, res.data].sort((a,b) => a.name.localeCompare(b.name)));
      } catch(e) { console.error("Excel add player failed:", p.name, e.message); }
    }
    setExcelReview(null);
  };

  const deletePlayer = async (id) => {
    if (!window.confirm("Delete this player?")) return;
    await api.delete("/players/" + id);
    setPlayers(prev => prev.filter(p => p.id !== id));
  };

  const updateEP = async (playerId, field, value) => {
    const ep = eventPlayers[playerId] || {};
    const updated = { ...ep, [field]: value, player_id: playerId };
    setEventPlayers(prev => ({ ...prev, [playerId]: updated }));
    if (!eventRef.current) return;
    try {
      await api.post("/events/" + eventRef.current.id + "/players", {
        player_id: playerId,
        playing: field === "playing" ? value : (ep.playing || false),
        buggy: field === "buggy" ? value : (ep.buggy || false),
        captain: field === "captain" ? value : (ep.captain || false),
        vice_captain: field === "vice_captain" ? value : (ep.vice_captain || false),
        hcp_review: field === "hcp_review" ? value : (ep.hcp_review || false),
      });
    } catch(e) { console.error("EP update:", e.message); }
  };

  const saveHcp = async (player) => {
    const val = parseInt(editHcp[player.id]);
    if (!isNaN(val)) {
      try {
        const res = await api.put("/players/" + player.id, { ...player, handicap: val });
        const saved = res.data.handicap;
        setPlayers(prev => prev.map(p => p.id === player.id ? { ...p, handicap: saved } : p));
        // The server silently clamps to Settings' Max Handicap -- surface that
        // instead of leaving the organiser to wonder why their entry reverted.
        if (saved !== val) {
          alert(`Handicap capped at ${saved} — Max Handicap in Handicap Settings is ${saved}. Entered value of ${val} was not saved.`);
        }
      } catch(e) { console.error("Save hcp:", e.message); }
    }
    setEditHcp(prev => { const n = { ...prev }; delete n[player.id]; return n; });
  };

  const saveHcpAdjOverride = async (player, rawVal) => {
    const trimmed = String(rawVal).trim();
    const value = trimmed === "" ? null : parseInt(trimmed);
    if (value !== null && isNaN(value)) {
      setEditHcpAdj(prev => { const n = { ...prev }; delete n[player.id]; return n; });
      return;
    }
    if (eventRef.current) {
      try {
        await api.put("/events/" + eventRef.current.id + "/players/" + player.id + "/hcp-adj-override", { value });
        // Re-fetch rather than reconstruct the calculated fallback locally
        // -- the slope/rating formula only lives server-side (utils/handicap.js)
        // on purpose, so clearing an override always shows the real
        // recalculated figure instead of a duplicated client-side guess.
        await loadPlayers();
        await loadOrCreateEvent();
      } catch(e) { console.error("Save Course HCP override:", e.message); }
    }
    setEditHcpAdj(prev => { const n = { ...prev }; delete n[player.id]; return n; });
  };

  const assignTeam = async (playerId, teamId) => {
    const ep = eventPlayers[playerId] || {};
    const newTeamId = ep.team_id === teamId ? null : teamId;
    setEventPlayers(prev => ({ ...prev, [playerId]: { ...ep, team_id: newTeamId, player_id: playerId } }));
    try {
      await api.post("/teams/assign", { playerId, teamId: newTeamId });
    } catch(e) { console.error("Assign team:", e.message); }
  };

  const th = "px-1 py-2 font-semibold border-r border-green-700 text-center break-words";
  const td = "px-1 py-2 border-r border-gray-300";
  const isHcpReviewFormat = HCP_REVIEW_FORMATS.includes(event?.game_type);

  // Only offer a Tee Color that genuinely exists for the linked course —
  // "gender: null" on a tee (the UK Golf Database doesn't always tag it)
  // means we can't rule either sex out, so both Men/Ladies labels for that
  // colour are offered rather than silently hiding one. No course linked,
  // or a course with no stored tee data (photo-import/manual courses never
  // have any), falls back to showing every color, exactly as before.
  const linkedCourse = courses.find(c => c.id === event?.course_id);
  const getAvailableTeeColors = (course) => {
    if (!course || !course.tee_sets || course.tee_sets.length === 0) return TEE_COLORS;
    const available = new Set();
    course.tee_sets.forEach(t => {
      const colour = (t.colour || "").trim().toLowerCase();
      const gender = t.gender;
      if (colour === "white") available.add("White");
      else if (colour === "yellow") {
        if (gender === "Female") available.add("Yellow (Ladies)");
        else if (gender === "Male") available.add("Yellow (Men)");
        else { available.add("Yellow (Men)"); available.add("Yellow (Ladies)"); }
      } else if (colour === "red") {
        if (gender === "Female") available.add("Red (Ladies)");
        else if (gender === "Male") available.add("Red (Men)");
        else { available.add("Red (Men)"); available.add("Red (Ladies)"); }
      }
    });
    return TEE_COLORS.filter(c => available.has(c));
  };
  const availableTeeColors = getAvailableTeeColors(linkedCourse);

  return (
    <Layout>
      <div className="flex flex-col h-full">
        {!isOnline && <OfflineViewingCachedDataBanner screen="Players" />}
        <div className="bg-white border-b border-gray-300 px-3 py-3 flex items-center gap-4 flex-wrap">
          <div className="flex items-center gap-1.5">
            <span className="text-base font-medium text-gray-600">Event Date</span>
            <input type="date" value={event?.date ? event.date.split("T")[0] : today}
              onChange={e => saveEvent("date", e.target.value)}
              className="border border-gray-300 rounded px-2 py-1.5 text-base w-40" />
          </div>
          <div className="flex items-center gap-1.5">
            <span className="text-base font-medium text-gray-600">Venue</span>
            <input type="text" value={event?.venue || ""} placeholder="Enter venue"
              onChange={e => setEvent(ev => ({...ev, venue: e.target.value}))}
              onBlur={e => saveEvent("venue", e.target.value)}
              className="border border-gray-300 rounded px-2 py-1.5 text-base w-36" />
          </div>
          <div className="flex items-center gap-1.5">
            <span className="text-base font-medium text-gray-600">Course</span>
            <select value={event?.course_id || ""}
              onChange={e => saveEvent("course_id", e.target.value ? parseInt(e.target.value) : null)}
              title="Which course is linked — drives available Tee Colours below"
              className="border border-gray-300 rounded px-2 py-1.5 text-base w-40">
              <option value="">— none —</option>
              {courses.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="text-base font-medium text-gray-600">Format</span>
            <select value={event?.game_type || "Stableford"}
              onChange={e => saveEvent("game_type", e.target.value)}
              className="border border-gray-300 rounded px-2 py-1.5 text-base">
              {gameTypes.map(g => <option key={g}>{g}</option>)}
            </select>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="text-base font-medium text-gray-600" title="Maximum Course HCP allowed — some clubs cap the adjusted handicap (e.g. a Course HCP of 36 gets capped to 32). Tick to override with a cap; untick for no cap.">HCP CAP</span>
            <input type="checkbox" checked={event?.handicap_cap != null}
              title="Override — tick to apply a cap, untick for no cap"
              onChange={e => {
                if (e.target.checked) {
                  const value = event?.handicap_cap ?? 36;
                  setEvent(ev => ({...ev, handicap_cap: value}));
                  saveEvent("handicap_cap", value);
                } else {
                  setEvent(ev => ({...ev, handicap_cap: null}));
                  saveEvent("handicap_cap", null);
                }
              }}
              className="w-5 h-5" />
            <input type="number" placeholder="none" value={event?.handicap_cap ?? ""}
              disabled={event?.handicap_cap == null}
              onChange={e => setEvent(ev => ({...ev, handicap_cap: e.target.value === "" ? null : parseInt(e.target.value)}))}
              onBlur={e => saveEvent("handicap_cap", e.target.value === "" ? null : parseInt(e.target.value))}
              className="border border-gray-300 rounded px-2 py-1.5 text-base w-20 disabled:bg-gray-100 disabled:text-gray-400" />
          </div>
          <div className="flex items-center gap-1.5">
            <span className="text-base font-medium text-gray-600">Team A Name</span>
            <input type="text" value={teamAName} onChange={e => setTeamAName(e.target.value)}
              onBlur={saveTeamNames} onKeyDown={e => e.key === "Enter" && saveTeamNames()}
              placeholder="e.g. Bromsgrove"
              className="border border-blue-300 rounded px-2 py-1.5 text-base w-32" />
          </div>
          <div className="flex items-center gap-1.5">
            <span className="text-base font-medium text-gray-600">Team B Name</span>
            <input type="text" value={teamBName} onChange={e => setTeamBName(e.target.value)}
              onBlur={saveTeamNames} onKeyDown={e => e.key === "Enter" && saveTeamNames()}
              placeholder="e.g. Ombersley"
              className="border border-red-300 rounded px-2 py-1.5 text-base w-32" />
          </div>
          <button onClick={updatePlayers} className="ml-auto bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold px-3 py-1.5 rounded">Update Players</button>
          <button onClick={() => showSaved("Saved!")} className="bg-green-600 hover:bg-green-700 text-white text-xs font-semibold px-3 py-1.5 rounded">Quick Save</button>
          <button onClick={openSaveModal} className="bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-semibold px-3 py-1.5 rounded">💾 Save Game</button>
          <button onClick={openLoadModal} className="bg-purple-600 hover:bg-purple-700 text-white text-xs font-semibold px-3 py-1.5 rounded">📂 Load Game</button>
          <button onClick={resetGame} className="bg-red-600 hover:bg-red-700 text-white text-xs font-semibold px-3 py-1.5 rounded">Reset Game</button>
        </div>
        {saved && <div style={{position:"fixed",top:"20px",left:"50%",transform:"translateX(-50%)",backgroundColor:"#dcfce7",color:"#14532d",padding:"10px 22px",borderRadius:"8px",fontWeight:"700",fontSize:"14px",zIndex:9999,boxShadow:"0 4px 12px rgba(0,0,0,0.2)"}}>{saved}</div>}

        {showSaveModal && (
          <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.5)",zIndex:10000,display:"flex",alignItems:"center",justifyContent:"center",padding:"20px"}}>
            <div style={{background:"white",borderRadius:"12px",padding:"24px",maxWidth:"420px",width:"100%",boxShadow:"0 8px 32px rgba(0,0,0,0.2)"}}>
              <div style={{fontSize:"16px",fontWeight:"700",color:"#3730a3",marginBottom:"8px"}}>💾 Save Game</div>
              <div style={{fontSize:"13px",color:"#374151",marginBottom:"16px"}}>
                Saves who's playing, groups, handicaps, teams and format for this event under a name you choose — recall it later to skip re-entering everything.
              </div>

              {savedGames.length > 0 && (
                <div style={{marginBottom:"18px"}}>
                  <label style={{fontSize:"12px",fontWeight:"600",color:"#374151",display:"block",marginBottom:"6px"}}>Update an existing save</label>
                  <div style={{maxHeight:"160px",overflowY:"auto",border:"1px solid #e5e7eb",borderRadius:"8px"}}>
                    {savedGames.map(g => (
                      <div key={g.id} style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:"10px",padding:"8px 12px",borderBottom:"1px solid #f3f4f6"}}>
                        <div style={{minWidth:0}}>
                          <div style={{fontSize:"13px",fontWeight:"600",color:"#1f2937",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{g.name}</div>
                          <div style={{fontSize:"11px",color:"#9ca3af"}}>{g.player_count} player(s)</div>
                        </div>
                        <button onClick={() => doUpdateGame(g)} disabled={saveBusy}
                          style={{background:"#eef2ff",color:"#4338ca",border:"1px solid #c7d2fe",borderRadius:"6px",padding:"4px 12px",fontSize:"12px",fontWeight:"700",cursor:"pointer",opacity:saveBusy?0.5:1,flexShrink:0}}>
                          Update
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <label style={{fontSize:"12px",fontWeight:"600",color:"#374151",display:"block",marginBottom:"6px"}}>{savedGames.length > 0 ? "Or save as a new name" : "Name"}</label>
              <input type="text" value={saveGameName} onChange={e => setSaveGameName(e.target.value)}
                placeholder="e.g. Celtic Manor Day 1 - Roman Road"
                onKeyDown={e => e.key === "Enter" && doSaveGame()}
                style={{width:"100%",border:"2px solid #a5b4fc",borderRadius:"8px",padding:"8px 12px",fontSize:"13px",boxSizing:"border-box",marginBottom:"16px"}}
                ref={saveGameInputRef} />
              <div style={{display:"flex",gap:"10px",justifyContent:"flex-end"}}>
                <button onClick={() => setShowSaveModal(false)}
                  style={{background:"none",border:"1px solid #d1d5db",borderRadius:"8px",padding:"8px 20px",fontSize:"13px",cursor:"pointer",color:"#6b7280"}}>
                  Cancel
                </button>
                <button onClick={doSaveGame} disabled={saveBusy || !saveGameName.trim()}
                  style={{background:"#4f46e5",color:"white",border:"none",borderRadius:"8px",padding:"8px 20px",fontSize:"13px",fontWeight:"700",cursor:"pointer",opacity:saveBusy||!saveGameName.trim()?0.5:1}}>
                  {saveBusy ? "Saving..." : "Save Game"}
                </button>
              </div>
            </div>
          </div>
        )}

        {showLoadModal && (
          <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.5)",zIndex:10000,display:"flex",alignItems:"center",justifyContent:"center",padding:"20px"}}>
            <div style={{background:"white",borderRadius:"12px",padding:"24px",maxWidth:"520px",width:"100%",boxShadow:"0 8px 32px rgba(0,0,0,0.2)"}}>
              <div style={{fontSize:"16px",fontWeight:"700",color:"#6b21a8",marginBottom:"4px"}}>📂 Load Game</div>

              <div style={{fontSize:"12px",fontWeight:"700",color:"#374151",margin:"14px 0 6px"}}>Competitions</div>
              <div style={{fontSize:"11px",color:"#9ca3af",marginBottom:"6px"}}>Real, dated competitions — deleting one removes it from the Terminal too.</div>
              <div style={{maxHeight:"180px",overflowY:"auto",border:"1px solid #e5e7eb",borderRadius:"8px",marginBottom:"18px"}}>
                {upcomingEvents.map(ev => (
                  <div key={ev.id} style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:"10px",padding:"10px 14px",borderBottom:"1px solid #f3f4f6"}}>
                    <div>
                      <div style={{fontSize:"13px",fontWeight:"700",color:"#374151"}}>{ev.venue}{ev.is_active && <span style={{marginLeft:"6px",fontSize:"10px",color:"#16a34a",fontWeight:"700"}}>ACTIVE</span>}</div>
                      <div style={{fontSize:"11px",color:"#9ca3af"}}>{ev.game_type} · {new Date(ev.date).toLocaleDateString("en-GB")}</div>
                    </div>
                    <button onClick={e => deleteCompetition(ev, e)}
                      style={{fontSize:"11px",color:"#ef4444",background:"none",border:"1px solid #fca5a5",borderRadius:"6px",padding:"4px 10px",cursor:"pointer",flexShrink:0}}>
                      Delete
                    </button>
                  </div>
                ))}
                {upcomingEvents.length === 0 && <div style={{padding:"16px",color:"#9ca3af",fontSize:"13px",textAlign:"center"}}>No upcoming competitions.</div>}
              </div>

              <div style={{fontSize:"12px",fontWeight:"700",color:"#374151",margin:"0 0 6px"}}>Saved Templates</div>
              <div style={{fontSize:"11px",color:"#9ca3af",marginBottom:"6px"}}>Reusable setup templates — loading one creates a new competition from it.</div>
              <div style={{maxHeight:"320px",overflowY:"auto",border:"1px solid #e5e7eb",borderRadius:"8px"}}>
                {savedGames.map(g => (
                  <div key={g.id} onClick={() => !loadBusy && doLoadGame(g)}
                    style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:"10px",padding:"10px 14px",borderBottom:"1px solid #f3f4f6",cursor:loadBusy?"default":"pointer"}}>
                    <div>
                      <div style={{fontSize:"13px",fontWeight:"700",color:"#374151"}}>{g.name}</div>
                      <div style={{fontSize:"11px",color:"#9ca3af"}}>{g.game_type} · {g.venue || "no venue"} · {g.player_count} player(s) · saved {new Date(g.created_at).toLocaleDateString("en-GB")}</div>
                    </div>
                    <button onClick={e => deleteSavedGame(g.id, e)}
                      style={{fontSize:"11px",color:"#ef4444",background:"none",border:"1px solid #fca5a5",borderRadius:"6px",padding:"4px 10px",cursor:"pointer",flexShrink:0}}>
                      Delete
                    </button>
                  </div>
                ))}
                {savedGames.length === 0 && <div style={{padding:"16px",color:"#9ca3af",fontSize:"13px",textAlign:"center"}}>No saved games yet.</div>}
              </div>
              <div style={{display:"flex",justifyContent:"flex-end",marginTop:"16px"}}>
                <button onClick={() => setShowLoadModal(false)}
                  style={{background:"none",border:"1px solid #d1d5db",borderRadius:"8px",padding:"8px 20px",fontSize:"13px",cursor:"pointer",color:"#6b7280"}}>
                  Close
                </button>
              </div>
            </div>
          </div>
        )}

        {voiceReview !== null && (
          <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.5)",zIndex:10000,display:"flex",alignItems:"center",justifyContent:"center",padding:"20px"}}>
            <div style={{background:"white",borderRadius:"12px",padding:"24px",maxWidth:"520px",width:"100%",boxShadow:"0 8px 32px rgba(0,0,0,0.2)"}}>
              <div style={{fontSize:"16px",fontWeight:"700",color:"#6b21a8",marginBottom:"8px"}}>🎤 Review Players</div>
              <div style={{fontSize:"13px",color:"#374151",marginBottom:"16px"}}>
                Check names and handicaps came out right, untick any you don't want, then add.
              </div>
              {voiceReview.length === 0 && <div style={{padding:"16px",color:"#9ca3af",fontSize:"13px",textAlign:"center"}}>Nothing to add.</div>}
              <div style={{maxHeight:"320px",overflowY:"auto"}}>
                {voiceReview.map((p, i) => (
                  <div key={i} style={{display:"flex",alignItems:"center",gap:"8px",padding:"8px 4px",borderBottom:"1px solid #f3f4f6"}}>
                    <input type="checkbox" checked={p.include}
                      onChange={e => setVoiceReview(rv => rv.map((r, idx) => idx===i ? {...r, include: e.target.checked} : r))} />
                    <input type="text" value={p.name}
                      onChange={e => setVoiceReview(rv => rv.map((r, idx) => idx===i ? {...r, name: e.target.value} : r))}
                      style={{flex:1,border:"1px solid #d1d5db",borderRadius:"6px",padding:"6px 8px",fontSize:"13px"}} />
                    <input type="number" value={p.handicap} min="0" max="54"
                      onChange={e => setVoiceReview(rv => rv.map((r, idx) => idx===i ? {...r, handicap: e.target.value} : r))}
                      style={{width:"56px",border:"1px solid #d1d5db",borderRadius:"6px",padding:"6px 8px",fontSize:"13px"}} />
                  </div>
                ))}
              </div>
              <div style={{display:"flex",gap:"10px",justifyContent:"flex-end",marginTop:"16px"}}>
                <button onClick={() => { setVoiceReview(null); setVoiceTranscript(""); }}
                  style={{background:"none",border:"1px solid #d1d5db",borderRadius:"8px",padding:"8px 20px",fontSize:"13px",cursor:"pointer",color:"#6b7280"}}>
                  Cancel
                </button>
                <button onClick={confirmVoicePlayers} disabled={!voiceReview.some(p => p.include && p.name.trim())}
                  style={{background:"#7e22ce",color:"white",border:"none",borderRadius:"8px",padding:"8px 20px",fontSize:"13px",fontWeight:"700",cursor:"pointer",opacity:!voiceReview.some(p => p.include && p.name.trim())?0.5:1}}>
                  Add Selected
                </button>
              </div>
            </div>
          </div>
        )}

        {excelReview !== null && (
          <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.5)",zIndex:10000,display:"flex",alignItems:"center",justifyContent:"center",padding:"20px"}}>
            <div style={{background:"white",borderRadius:"12px",padding:"24px",maxWidth:"520px",width:"100%",boxShadow:"0 8px 32px rgba(0,0,0,0.2)"}}>
              <div style={{fontSize:"16px",fontWeight:"700",color:"#166534",marginBottom:"8px"}}>📊 Review Imported Players</div>
              <div style={{fontSize:"13px",color:"#374151",marginBottom:"16px"}}>
                Check names and handicaps came out right, untick any you don't want, then add.
              </div>
              {excelReview.length === 0 && <div style={{padding:"16px",color:"#9ca3af",fontSize:"13px",textAlign:"center"}}>Nothing to add.</div>}
              <div style={{maxHeight:"320px",overflowY:"auto"}}>
                {excelReview.map((p, i) => (
                  <div key={i} style={{display:"flex",alignItems:"center",gap:"8px",padding:"8px 4px",borderBottom:"1px solid #f3f4f6"}}>
                    <input type="checkbox" checked={p.include}
                      onChange={e => setExcelReview(rv => rv.map((r, idx) => idx===i ? {...r, include: e.target.checked} : r))} />
                    <input type="text" value={p.name}
                      onChange={e => setExcelReview(rv => rv.map((r, idx) => idx===i ? {...r, name: e.target.value} : r))}
                      style={{flex:1,border:"1px solid #d1d5db",borderRadius:"6px",padding:"6px 8px",fontSize:"13px"}} />
                    <input type="number" value={p.handicap} min="0" max="54"
                      onChange={e => setExcelReview(rv => rv.map((r, idx) => idx===i ? {...r, handicap: e.target.value} : r))}
                      style={{width:"56px",border:"1px solid #d1d5db",borderRadius:"6px",padding:"6px 8px",fontSize:"13px"}} />
                  </div>
                ))}
              </div>
              <div style={{display:"flex",gap:"10px",justifyContent:"flex-end",marginTop:"16px"}}>
                <button onClick={() => setExcelReview(null)}
                  style={{background:"none",border:"1px solid #d1d5db",borderRadius:"8px",padding:"8px 20px",fontSize:"13px",cursor:"pointer",color:"#6b7280"}}>
                  Cancel
                </button>
                <button onClick={confirmExcelPlayers} disabled={!excelReview.some(p => p.include && p.name.trim())}
                  style={{background:"#166534",color:"white",border:"none",borderRadius:"8px",padding:"8px 20px",fontSize:"13px",fontWeight:"700",cursor:"pointer",opacity:!excelReview.some(p => p.include && p.name.trim())?0.5:1}}>
                  Add Selected
                </button>
              </div>
            </div>
          </div>
        )}
        <div className="bg-white border-b border-gray-300 px-3 py-3 flex items-center gap-4 flex-wrap">
          <span className="text-base font-medium text-gray-700">Add Player:</span>
          <input type="text" value={newName} onChange={e => setNewName(e.target.value)}
            placeholder="Full name (Surname First)"
            className="border border-gray-300 rounded px-2 py-1.5 text-base w-64"
            onKeyDown={e => e.key === "Enter" && addPlayer()} />
          <span className="text-base font-medium text-gray-700">Handicap:</span>
          <input type="number" value={newHcp} onChange={e => setNewHcp(e.target.value)}
            placeholder="0-54" min="0" max="54"
            className="border border-gray-300 rounded px-2 py-1.5 text-base w-20"
            onKeyDown={e => e.key === "Enter" && addPlayer()} />
          <span className="text-base font-medium text-gray-700">Tee Colour:</span>
          <select value={newTeeColor} onChange={e => setNewTeeColor(e.target.value)}
            className="border border-gray-300 rounded px-2 py-1.5 text-base">
            <option value="">—</option>
            {availableTeeColors.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
          <button onClick={addPlayer} className="bg-green-600 hover:bg-green-700 text-white text-xs font-semibold px-3 py-1.5 rounded">Add Player</button>
          {selectedId && <button onClick={() => deletePlayer(selectedId)} className="bg-red-600 hover:bg-red-700 text-white text-xs font-semibold px-3 py-1.5 rounded">Delete Player</button>}
          {!voiceListening && !isElectron && isOnline && (
            <button onClick={startVoiceInput} title="Speak player names and handicaps instead of typing — Chrome/Edge only"
              className="bg-purple-600 hover:bg-purple-700 text-white text-xs font-semibold px-3 py-1.5 rounded">🎤 Voice Add Players</button>
          )}
          {!voiceListening && !isElectron && !isOnline && (
            <span className="text-xs text-gray-400 italic" title="Voice input needs an internet connection">🎤 Voice Add Players (needs signal)</span>
          )}
          {!voiceListening && isElectron && (
            <span className="text-xs text-gray-400 italic" title="Not available in the desktop app — use the web version">🎤 Voice Add Players (web only)</span>
          )}
          {voiceListening && (
            <button onClick={stopVoiceInput}
              className="bg-red-600 hover:bg-red-700 text-white text-xs font-semibold px-3 py-1.5 rounded animate-pulse">⏹ Stop Listening</button>
          )}
          <input ref={excelFileInputRef} type="file" accept=".xls,.xlsx,.csv" style={{display:"none"}} onChange={handleExcelFile} />
          <button onClick={pickExcelFile} disabled={excelBusy} title="Import players and handicaps from an Excel/CSV spreadsheet — any layout, extra columns are fine"
            className="bg-green-700 hover:bg-green-800 text-white text-xs font-semibold px-3 py-1.5 rounded disabled:opacity-50">
            📊 {excelBusy ? "Reading..." : "Import Players from XL"}
          </button>
          <button onClick={() => window.location.reload()} title="Refresh this page"
            className="bg-gray-600 hover:bg-gray-700 text-white text-xs font-semibold px-3 py-1.5 rounded">
            🔄 Refresh
          </button>
        </div>
        {voiceListening && (
          <div className="bg-purple-50 border-b border-purple-200 px-4 py-2 text-sm text-purple-800">
            🎤 Listening — read out names and handicaps, e.g. "Michael Sellman, with 2 L's, handicap 14". Click Stop when done.
            <div className="italic text-purple-600 mt-1">{voiceTranscript || "..."}</div>
          </div>
        )}
        {voiceBusy && <div className="bg-purple-50 border-b border-purple-200 px-4 py-2 text-sm text-purple-800">Working out who you said...</div>}
        {voiceError && <div className="bg-red-100 text-red-700 px-4 py-2 text-sm font-medium">{voiceError}</div>}
        {excelBusy && <div className="bg-green-50 border-b border-green-200 px-4 py-2 text-sm text-green-800">Reading spreadsheet and working out which columns are name and handicap...</div>}
        {excelError && <div className="bg-red-100 text-red-700 px-4 py-2 text-sm font-medium">{excelError}</div>}
        {error && <div className="bg-red-100 text-red-700 px-4 py-2 text-sm font-medium">{error}</div>}
        {loading && <div className="bg-blue-50 text-blue-700 px-4 py-2 text-sm">Loading players...</div>}
        <div className="flex-1 overflow-auto">
          <table className="w-full table-fixed text-sm border-collapse border border-gray-300">
            <colgroup>
              <col style={{width: isHcpReviewFormat ? "16%" : "18%"}} />
              <col style={{width:"7%"}} />
              <col style={{width:"7%"}} />
              <col style={{width:"7%"}} />
              <col style={{width:"7%"}} />
              <col style={{width:"7%"}} />
              {isHcpReviewFormat && <col style={{width:"7%"}} />}
              <col style={{width:"9%"}} />
              <col style={{width:"9%"}} />
            </colgroup>
            <thead className="sticky top-0">
              <tr className="bg-green-800 text-white">
                <th className={th + " text-left"}>Member</th>
                <th className={th} title="World Handicap System Handicap Index">WHS HCP</th>
                <th className={th} title="Course Handicap: WHS HCP adjusted for the linked course's slope/rating for this player's own Tee Colour, capped at HCP CAP if set. Editable -- click a value to manually override it for this event only.">
                  <div className="flex flex-col items-center gap-1">
                    <span>Course HCP</span>
                    <span className="bg-red-600 text-white text-[9px] font-bold px-1.5 py-0.5 rounded leading-tight">EDIT WITH CAUTION</span>
                  </div>
                </th>
                <th className={th}>Playing</th>
                <th className={th}>Buggy</th>
                <th className={th}>Captain</th>
                <th className={th}>Vice Captain</th>
                {isHcpReviewFormat && <th className={th}>HCP Review</th>}
                <th className={th}>{teamAName || "Team A"}</th>
                <th className={th}>{teamBName || "Team B"}</th>
              </tr>
            </thead>
            <tbody>
              {players.map((player, i) => {
                const ep = eventPlayers[player.id] || {};
                return (
                  <tr key={player.id} onClick={() => setSelectedId(player.id)} className={"cursor-pointer border-b border-gray-200 " + (selectedId === player.id ? "bg-blue-100" : i % 2 === 0 ? "bg-white" : "bg-green-50")}>
                    <td className={td + " font-medium"}>{player.name}</td>
                    <td className={td + " text-center"}>
                      {editHcp[player.id] !== undefined ? (
                        <input type="number" value={editHcp[player.id]}
                          onChange={e => setEditHcp(prev => ({ ...prev, [player.id]: e.target.value }))}
                          onBlur={() => saveHcp(player)}
                          onKeyDown={e => e.key === "Enter" && saveHcp(player)}
                          className="border border-green-400 rounded px-1 py-0.5 w-16 text-xs text-center" autoFocus />
                      ) : (
                        <span className="cursor-pointer hover:text-green-700 font-semibold"
                          onClick={() => setEditHcp(prev => ({ ...prev, [player.id]: player.handicap }))}>
                          {player.handicap}
                        </span>
                      )}
                    </td>
                    <td className={td + " text-center text-gray-600"} onClick={e => e.stopPropagation()}>
                      {(() => {
                        const effectiveHcpAdj = eventPlayers[player.id]?.hcp_adj ?? player.hcp_adj ?? player.handicap;
                        const isOverridden = (eventPlayers[player.id]?.hcp_adj_override ?? player.hcp_adj_override) != null;
                        return editHcpAdj[player.id] !== undefined ? (
                          <input type="number" value={editHcpAdj[player.id]}
                            onChange={e => setEditHcpAdj(prev => ({ ...prev, [player.id]: e.target.value }))}
                            onBlur={() => saveHcpAdjOverride(player, editHcpAdj[player.id])}
                            onKeyDown={e => e.key === "Enter" && saveHcpAdjOverride(player, editHcpAdj[player.id])}
                            placeholder="blank = auto"
                            className="border border-red-400 rounded px-1 py-0.5 w-16 text-xs text-center" autoFocus />
                        ) : (
                          <span className="cursor-pointer hover:text-red-700"
                            title={isOverridden ? "Manually overridden for this event -- click to change, clear the box to restore the calculated value" : "Click to manually override this player's Course HCP for this event"}
                            onClick={() => setEditHcpAdj(prev => ({ ...prev, [player.id]: effectiveHcpAdj }))}>
                            {effectiveHcpAdj}
                            {isOverridden && <span className="ml-1 text-red-600 font-bold" title="Manual override active">*</span>}
                          </span>
                        );
                      })()}
                    </td>
                    <td className={td + " text-center"}><input type="checkbox" checked={!!ep.playing} onChange={e => updateEP(player.id, "playing", e.target.checked)} className="w-4 h-4" /></td>
                    <td className={td + " text-center"}><input type="checkbox" checked={!!ep.buggy} onChange={e => updateEP(player.id, "buggy", e.target.checked)} className="w-4 h-4" /></td>
                    <td className={td + " text-center"}><input type="checkbox" checked={!!ep.captain} onChange={e => updateEP(player.id, "captain", e.target.checked)} className="w-4 h-4" /></td>
                    <td className={td + " text-center"}><input type="checkbox" checked={!!ep.vice_captain} onChange={e => updateEP(player.id, "vice_captain", e.target.checked)} className="w-4 h-4" /></td>
                    {isHcpReviewFormat && <td className={td + " text-center"}><input type="checkbox" checked={!!ep.hcp_review} onChange={e => updateEP(player.id, "hcp_review", e.target.checked)} className="w-4 h-4" /></td>}
                    <td className={td + " text-center"}>
                      <input type="checkbox" checked={!!(teams[0] && ep.team_id === teams[0].id)}
                        onChange={() => assignTeam(player.id, teams[0] ? teams[0].id : null)}
                        disabled={!teams[0]} className="w-4 h-4" />
                    </td>
                    <td className={td + " text-center"}>
                      <input type="checkbox" checked={!!(teams[1] && ep.team_id === teams[1].id)}
                        onChange={() => assignTeam(player.id, teams[1] ? teams[1].id : null)}
                        disabled={!teams[1]} className="w-4 h-4" />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {!loading && players.length === 0 && !error && (
            <div className="text-center py-8 text-gray-500">No players yet. Add your first player above.</div>
          )}
        </div>
      </div>
    </Layout>
  );
}
